import React, { useEffect, useState } from "react";
import { Loader2, UserPlus } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BLANK = {
  full_name: "", position: "", department: "", employment_type: "",
  hire_date: "", email: "", phone: "", home_address: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  service_area: "", supervisor: "", pay_type: "", primary_lab_partner: "",
  notes: "",
};

export default function AddEmployeeDialog({ open, onClose, onCreate }) {
  const [form, setForm] = useState(BLANK);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setForm(BLANK); setBusy(false); } }, [open]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    if (!form.full_name.trim()) return;
    setBusy(true);
    try {
      // Strip empty strings so backend defaults kick in
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "" && v != null)
      );
      await onCreate(payload);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="add-employee-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ color: "#0B2545" }}>
            <UserPlus className="h-5 w-5" /> Add employee
          </DialogTitle>
          <DialogDescription>
            Creates a new employee profile with a 20-item onboarding checklist. Employee ID is auto-generated.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2 mt-3">
          <Field label="Full name *">
            <Input value={form.full_name} onChange={set("full_name")}
                   placeholder="e.g. Jasmine Negron" data-testid="add-employee-name" />
          </Field>
          <Field label="Position">
            <Input value={form.position} onChange={set("position")}
                   placeholder="Phlebotomist" data-testid="add-employee-position" />
          </Field>
          <Field label="Department">
            <Input value={form.department} onChange={set("department")}
                   placeholder="Clinical Services" data-testid="add-employee-department" />
          </Field>
          <Field label="Employment type">
            <Input value={form.employment_type} onChange={set("employment_type")}
                   placeholder="1099 Contractor / W-2 Employee" data-testid="add-employee-emp-type" />
          </Field>
          <Field label="Hire date">
            <Input type="date" value={form.hire_date} onChange={set("hire_date")}
                   data-testid="add-employee-hire-date" />
          </Field>
          <Field label="Supervisor">
            <Input value={form.supervisor} onChange={set("supervisor")}
                   placeholder="Beckett / Jazmin / …" data-testid="add-employee-supervisor" />
          </Field>
          <Field label="Email">
            <Input type="email" value={form.email} onChange={set("email")}
                   placeholder="jasmine@tjclinical.solutions" data-testid="add-employee-email" />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={set("phone")}
                   placeholder="(732) 555-0184" data-testid="add-employee-phone" />
          </Field>
          <Field label="Emergency contact name">
            <Input value={form.emergency_contact_name} onChange={set("emergency_contact_name")}
                   data-testid="add-employee-emergency-name" />
          </Field>
          <Field label="Emergency contact phone">
            <Input value={form.emergency_contact_phone} onChange={set("emergency_contact_phone")}
                   data-testid="add-employee-emergency-phone" />
          </Field>
          <Field label="Service area">
            <Input value={form.service_area} onChange={set("service_area")}
                   placeholder="Ocean County, NJ" data-testid="add-employee-service-area" />
          </Field>
          <Field label="Pay type">
            <Input value={form.pay_type} onChange={set("pay_type")}
                   placeholder="Hourly / Per-visit / Salaried" data-testid="add-employee-pay-type" />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Home address">
              <Input value={form.home_address} onChange={set("home_address")}
                     data-testid="add-employee-address" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Primary lab partner">
              <Input value={form.primary_lab_partner} onChange={set("primary_lab_partner")}
                     placeholder="Labcorp / Quest / …" data-testid="add-employee-lab-partner" />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Internal notes">
              <Textarea rows={3} value={form.notes} onChange={set("notes")}
                        data-testid="add-employee-notes" />
            </Field>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}
                  data-testid="add-employee-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || !form.full_name.trim()}
                  className="h-10" style={{ background: "#0B2545", color: "#FFD774" }}
                  data-testid="add-employee-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create employee"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.18em] font-medium mb-1" style={{ color: "#0B2545" }}>
      {label}
    </div>
    {children}
  </div>
);
